import {
  buildReclaimReferralCodeIntakeAnswers,
  getReclaimReferralCodeIntakeState,
} from "@redotech/redo-dispute-common/reclaim-referral-code-intake";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import XCircleIcon from "@redotech/redo-icons/x-circle.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDebounce } from "usehooks-ts";
import * as css from "../../components/intake-modal/intake-modal.module.css";
import type { IntakeAdditionalStepProps } from "../../registry/types";

const UNAVAILABLE_CODE_COPY =
  "We couldn't apply that code. Check it and try again, or continue with your standard setup.";
const MIN_LIVE_VALIDATION_LENGTH = 3;

type ReferralCodeValidation =
  | { status: "idle" }
  | { status: "checking"; code: string }
  | { status: "available"; code: string }
  | { status: "unavailable"; code: string };

type ReferralCodeValidationIndicatorStatus =
  | "checking"
  | "available"
  | "unavailable";

type ReclaimReferralCodeRpcClient = Pick<
  IntakeAdditionalStepProps["rpcClient"],
  "redeemReclaimReferralCode" | "validateReclaimReferralCode"
>;

export interface ReclaimReferralCodeStepProps extends Omit<
  IntakeAdditionalStepProps,
  "rpcClient"
> {
  rpcClient: ReclaimReferralCodeRpcClient;
}

export const RECLAIM_REFERRAL_CODE_STEP_ID = "partner-code";

export function writeReclaimReferralCodeAnswers({
  setAnswer,
  state,
}: {
  setAnswer: IntakeAdditionalStepProps["setAnswer"];
  state: Parameters<typeof buildReclaimReferralCodeIntakeAnswers>[0];
}) {
  for (const [answerId, value] of Object.entries(
    buildReclaimReferralCodeIntakeAnswers(state),
  )) {
    setAnswer(answerId, value);
  }
}

export const ReclaimReferralCodeStep = memo(function ReclaimReferralCodeStep({
  answers,
  onContinue,
  onFooterChromeChange,
  rpcClient,
  setAnswer,
}: ReclaimReferralCodeStepProps) {
  const state = getReclaimReferralCodeIntakeState(answers);
  const [code, setCode] = useState(state.referralCode ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(
    state.status === "unavailable" ? UNAVAILABLE_CODE_COPY : null,
  );
  const applied = state.status === "applied";
  const mountedRef = useRef(true);

  const normalizedCode = code.trim().toUpperCase();
  const debouncedNormalizedCode = useDebounce(normalizedCode, 300);
  const [validation, setValidation] = useState<ReferralCodeValidation>({
    status: "idle",
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (applied) {
      return;
    }
    if (debouncedNormalizedCode.length < MIN_LIVE_VALIDATION_LENGTH) {
      setValidation({ status: "idle" });
      return;
    }

    let canceled = false;
    setValidation({ code: debouncedNormalizedCode, status: "checking" });
    void rpcClient
      .validateReclaimReferralCode({ code: debouncedNormalizedCode })
      .then((result) => {
        if (canceled || !mountedRef.current) {
          return;
        }
        setValidation({
          code: debouncedNormalizedCode,
          status: result.status === "available" ? "available" : "unavailable",
        });
      })
      .catch(() => {
        if (!canceled && mountedRef.current) {
          setValidation({ status: "idle" });
        }
      });

    return () => {
      canceled = true;
    };
  }, [applied, debouncedNormalizedCode, rpcClient]);

  const matchingValidation =
    validation.status !== "idle" && validation.code === normalizedCode
      ? validation
      : null;
  const codeCanBeApplied =
    matchingValidation?.status === "available" && !pending && !applied;
  const isAwaitingValidation =
    !applied &&
    normalizedCode.length >= MIN_LIVE_VALIDATION_LENGTH &&
    (normalizedCode !== debouncedNormalizedCode ||
      matchingValidation?.status === "checking");
  const indicatorStatus: ReferralCodeValidationIndicatorStatus | null = applied
    ? "available"
    : matchingValidation?.status === "available"
      ? "available"
      : matchingValidation?.status === "unavailable"
        ? "unavailable"
        : isAwaitingValidation
          ? "checking"
          : null;

  const applyCode = useCallback(async () => {
    if (!normalizedCode || !codeCanBeApplied) {
      return;
    }
    setPending(true);
    setError(null);
    let shouldContinue = false;
    try {
      const result = await rpcClient.redeemReclaimReferralCode({
        code: normalizedCode,
      });
      if (!mountedRef.current) {
        return;
      }
      if (
        result.status === "redeemed" ||
        result.status === "already_redeemed"
      ) {
        writeReclaimReferralCodeAnswers({
          setAnswer,
          state: {
            referrerName: result.referrerName,
            referrerProfileImageUrl: result.profileImageUrl ?? undefined,
            referralCode: result.referralCode ?? normalizedCode,
            status: "applied",
          },
        });
        shouldContinue = true;
      } else {
        writeReclaimReferralCodeAnswers({
          setAnswer,
          state: { referralCode: normalizedCode, status: "unavailable" },
        });
        setError(UNAVAILABLE_CODE_COPY);
      }
    } catch {
      if (mountedRef.current) {
        setError("Could not apply that code. Check it and try again.");
      }
    } finally {
      if (mountedRef.current) {
        setPending(false);
      }
    }
    if (shouldContinue && mountedRef.current) {
      onContinue();
    }
  }, [codeCanBeApplied, normalizedCode, onContinue, rpcClient, setAnswer]);

  useEffect(() => {
    onFooterChromeChange({
      continueDisabled: applied ? false : !codeCanBeApplied,
      continuePending: pending,
      continueText: applied ? "Continue" : "Apply code",
      onContinue: applied ? onContinue : applyCode,
    });
  }, [
    applied,
    applyCode,
    codeCanBeApplied,
    onContinue,
    onFooterChromeChange,
    pending,
  ]);

  useEffect(
    () => () => {
      onFooterChromeChange(null);
    },
    [onFooterChromeChange],
  );

  return (
    <div className={css.questionsBlock}>
      <Text as="h1" className={css.title}>
        Referral code
      </Text>
      <Text fontSize="md" mt="lg" textColor="secondary">
        If someone referred you to Redo and gave you a code, enter it here.
        Otherwise, continue with your standard Reclaim setup.
      </Text>
      <Flex dir="column" gap="lg" mt="2xl" w="full">
        <RedoTextInput
          ariaLabel="Referral code"
          autoCompleteDisabled
          autoFocus
          errorText={error ?? undefined}
          fullWidth
          maxLength={20}
          placeholder="Enter referral code"
          setValue={(value) => {
            setCode(value.toUpperCase());
            setError(null);
          }}
          size="lg"
          state={pending || applied ? "disabled" : "default"}
          trailingActions={
            indicatorStatus ? (
              <ReferralCodeValidationIndicator status={indicatorStatus} />
            ) : undefined
          }
          value={code}
        />
        {applied && (
          <Text fontSize="sm" textColor="success">
            {state.referrerName
              ? `Welcome from ${state.referrerName}. Your referral offer has been applied.`
              : "Referral code applied. Your referral offer has been applied."}
          </Text>
        )}
      </Flex>
    </div>
  );
});

interface ReferralCodeValidationIndicatorProps {
  status: ReferralCodeValidationIndicatorStatus;
}

const ReferralCodeValidationIndicator = memo(
  function ReferralCodeValidationIndicator({
    status,
  }: ReferralCodeValidationIndicatorProps) {
    const label =
      status === "available"
        ? "Referral code is valid"
        : status === "unavailable"
          ? "Referral code is not valid"
          : "Checking referral code";

    return (
      <Flex
        align="center"
        aria-label={label}
        left="md"
        ml="xxs"
        position="relative"
      >
        {status === "checking" ? (
          <RedoSpinner height={20} size="xs" style="line" width={20} />
        ) : (
          <RedoIcon
            color={status === "available" ? "success" : "error"}
            Icon={status === "available" ? CheckCircleIcon : XCircleIcon}
            size={20}
          />
        )}
      </Flex>
    );
  },
);
